import { GitWriteLock } from "./useGitWriteLock";

/**
 * Decide whether a Console client should raise a "Commit finished" success
 * toast for a git write lock state transition.
 *
 * The git write lock is workspace-global and its WRITE_SUCCEEDED state is
 * broadcast over the websocket to EVERY connected client on EVERY git write --
 * including the frequent background auto-commits that every tracked-record save
 * triggers. Toasting on all of those would spam the UI. A success toast is
 * raised only when:
 *   1. this is a NEW success (transition into WRITE_SUCCEEDED, not a repeat),
 *   2. the write is an explicit user Commit -- the backend tags the lock with
 *      `operation === "commit"` for the single-record Commit action and
 *      `operation === "bulk_commit"` for the container Bulk commit action,
 *      leaving the background auto-commits unmarked, and
 *   3. the write was interactive (a human in the Console) rather than a
 *      system/background actor (a running flow / internal engine/git-sync
 *      call). A system-initiated commit is not something a connected user
 *      performed, so a toast to all of them is noise.
 *   4. the commit was initiated by THIS session's user -- the lock is
 *      workspace-global and WRITE_SUCCEEDED is broadcast to every connected
 *      client, so without this check every user is toasted for every user's
 *      commit (and, notably, for the operator's continuous brain-CO commits).
 *      The toast is shown only to the committer.
 *
 * `operation`, `interactive` and `initiated_by` are emitted by the backend
 * git_write_lock payload. `operation` is absent on payloads from older backends
 * and on all non-commit writes, so an absent value (or any value other than
 * "commit"/"bulk_commit") suppresses the toast -- the completion toast is
 * strictly opt-in to the explicit Commit actions. `initiated_by` is also absent
 * on older backends; when it or the
 * current user id is unknown, no one is toasted (only the committer should be,
 * and we cannot prove a match) -- accepted as the safe default over the prior
 * toast-everyone behaviour.
 *
 * See operator-task-fe-commit-finished-toast-scope-to-initiating-session
 * (and operator-task-fe-commit-completion-signal-graphql-broadcast).
 */
export const shouldNotifyGitWriteSuccess = (
    current: GitWriteLock | undefined,
    previous: GitWriteLock | undefined,
    currentUserId?: string
): boolean => {
    const isNewSuccess =
        current?.state === "WRITE_SUCCEEDED" && previous?.state !== "WRITE_SUCCEEDED";
    const isExplicitCommit =
        current?.operation === "bulk_commit" || current?.operation === "commit";
    const isSystemInitiated = current?.interactive === false;
    const initiatedByThisUser =
        !!currentUserId && current?.initiated_by === currentUserId;
    return isNewSuccess && isExplicitCommit && !isSystemInitiated && initiatedByThisUser;
};
